import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import {
  OptionContractLookupRow,
  OrderPreview,
  OrderTicketPrefill,
  PlaceOrderRequest,
  TickL1,
} from '../../../types/trading';
import { OrderTicket } from '../manual/OrderTicket';
import { BacktestConfig } from '../strategy-backtest/BacktestConfig';
import type { StrategyTicketContext } from '../strategy-backtest/types/context';
import {
  buildOrderTicketPersistenceScope,
  detectSessionIdFromPath,
} from '../persistence/orderTicketPersistence';
import styles from './OrderTicketNavigationMenu.module.css';
import tradingStyles from '../../../components/trading/Trading.module.css';

export type OrderTicketPaneMode = 'manual' | 'strategy';

interface OrderTicketNavigationMenuProps {
  isOpen: boolean;
  onClose: () => void;
  mode: OrderTicketPaneMode;
  onModeChange: (mode: OrderTicketPaneMode) => void;
  strategyContext: StrategyTicketContext;
  isStrategyModeLocked: boolean;
  pathname: string;
  prefill: OrderTicketPrefill | null;
  ticksBySymbol: Record<string, TickL1>;
  onSubscribeSymbol: (symbol: string) => void;
  onUnsubscribeSymbol: (symbol: string) => void;
  onPreview: (request: PlaceOrderRequest) => Promise<OrderPreview>;
  onPlaceOrder: (request: PlaceOrderRequest) => Promise<unknown>;
  isSimulationEnabled: boolean | null;
  onFetchMarketOpen: (symbol: string) => Promise<boolean>;
  onLoadOptionContractsByType: (
    underlying: string,
    optionType: 'call' | 'put',
  ) => Promise<OptionContractLookupRow[]>;
  onLoadOptionContractsByStrike: (
    underlying: string,
    optionType: 'call' | 'put',
    strike: number,
  ) => Promise<OptionContractLookupRow[]>;
  onLoadOptionContractsByExpiration: (
    underlying: string,
    optionType: 'call' | 'put',
    expiration: string,
  ) => Promise<OptionContractLookupRow[]>;
  disabled?: boolean;
}

export const OrderTicketNavigationMenu: React.FC<OrderTicketNavigationMenuProps> = ({
  isOpen,
  onClose,
  mode,
  onModeChange,
  strategyContext,
  isStrategyModeLocked,
  pathname,
  prefill,
  ticksBySymbol,
  onSubscribeSymbol,
  onUnsubscribeSymbol,
  onPreview,
  onPlaceOrder,
  isSimulationEnabled,
  onFetchMarketOpen,
  onLoadOptionContractsByType,
  onLoadOptionContractsByStrike,
  onLoadOptionContractsByExpiration,
  disabled = false,
}) => {
  const activeSessionId = detectSessionIdFromPath(pathname);
  const persistenceScope = buildOrderTicketPersistenceScope(pathname, strategyContext);
  const strategyTabDisabled = isStrategyModeLocked && mode !== 'strategy';

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  return (
    <>
      <div
        className={`${styles.overlay} ${isOpen ? styles.open : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      <aside
        className={`${styles.panel} ${isOpen ? styles.open : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Order ticket panel"
      >
        <div className={styles.panelHeader} role="tablist" aria-label="Order ticket mode">
          <button
            type="button"
            className={`${styles.headerTab} ${mode === 'manual' ? styles.headerTabActive : ''}`}
            aria-pressed={mode === 'manual'}
            onClick={() => onModeChange('manual')}
            data-testid="order-pane-mode-manual"
          >
            Manual Order
          </button>
          <button
            type="button"
            className={`${styles.headerTab} ${mode === 'strategy' ? styles.headerTabActive : ''}`}
            aria-pressed={mode === 'strategy'}
            onClick={() => onModeChange('strategy')}
            disabled={strategyTabDisabled}
            title={strategyTabDisabled ? 'Strategy Order is unavailable while AI is responding' : 'Strategy Order'}
            data-testid="order-pane-mode-strategy"
          >
            Strategy Order
          </button>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close order ticket drawer"
          >
            <X size={18} />
          </button>
        </div>

        <div className={styles.disclaimerBox} role="note" aria-label="Order ticket strategy disclaimer">
          <p className={styles.disclaimerTitle}>Note:</p>
          <ol className={styles.disclaimerList}>
            <li>
              Intraday mode supports stock-only strategies with MA Cross signals.
            </li>
            <li>
              When Intraday is off, strategies are traded at end of day around 3:45 PM, before the closing auction starts.
            </li>
            <li>
              All strategy orders will be executed at Best Fill, which is the available mid price.
            </li>
          </ol>
        </div>

        {mode === 'manual' ? (
          <div className={`${styles.panelContent} ${tradingStyles.tradingTheme}`}>
            <OrderTicket
              selectedSymbol={null}
              ticksBySymbol={ticksBySymbol}
              onSubscribeSymbol={onSubscribeSymbol}
              onUnsubscribeSymbol={onUnsubscribeSymbol}
              onPreview={onPreview}
              onPlaceOrder={onPlaceOrder}
              isSimulationEnabled={isSimulationEnabled}
              onFetchMarketOpen={onFetchMarketOpen}
              onLoadOptionContractsByType={onLoadOptionContractsByType}
              onLoadOptionContractsByStrike={onLoadOptionContractsByStrike}
              onLoadOptionContractsByExpiration={onLoadOptionContractsByExpiration}
              closeRequest={prefill}
              disabled={disabled}
              persistenceScope={persistenceScope}
              onOrderPlaced={onClose}
            />
          </div>
        ) : (
          <div className={styles.panelContent}>
            <BacktestConfig
              sessionId={activeSessionId}
              context={strategyContext}
              persistenceScope={persistenceScope}
            />
          </div>
        )}
      </aside>
    </>
  );
};
